import React, { createContext, useContext, } from "react";

export type StepperContextType = {
  orientation: "horizontal" | "vertical";
  alternativeLabel: boolean;
}

export type StepContextType = {
  index: number;
  active: boolean;
  completed: boolean;
  isLast: boolean;
}

const StepperContext = createContext<StepperContextType | null>(null);
const StepContext = createContext<StepContextType | null>(null);

function useStepperContext() {
  const context = useContext(StepperContext);

  if (!context) throw new Error("Componentes do Stepper devem estar dentro de <Stepper>.");

  return context;
}

function useStepContext() {
  const context = useContext(StepContext);

  if (!context) throw new Error("Componentes do Step devem estar dentro de <Step>.");

  return context;
}

export type StepperProps = React.HTMLAttributes<HTMLDivElement> & {
  activeStep?: number;
  alternativeLabel?: boolean;
  children: React.ReactNode;
  orientation?: "horizontal" | "vertical";
}

export function Stepper({
  activeStep = 0,
  alternativeLabel = false,
  orientation = "horizontal",
  children,
  className = "",
  ...props
}: StepperProps): JSX.Element {
  const childrenArray = React.Children.toArray(children);

  const steps = React.Children.map(childrenArray, (child, index) => {
    if (!React.isValidElement(child)) return child;

    const isLast = index === childrenArray.length - 1;
    // Se o filho não receber props explícitas, o Stepper calcula com base no activeStep
    const active = child.props.active !== undefined ? child.props.active : index === activeStep;
    const completed = child.props.completed !== undefined ? child.props.completed : index < activeStep;

    return React.cloneElement(child as React.ReactElement, {
      index,
      isLast,
      active,
      completed,
      ...child.props,
    });
  });

  return (
    <StepperContext.Provider value={{ orientation, alternativeLabel }}>
      <div
        className={`flex ${
          orientation === "vertical" ? "flex-col" : "flex-row items-center justify-between"
        } ${className}`}
        {...props}
      >
        {steps}
      </div>
    </StepperContext.Provider>
  );
}

// --- STEP ---
export type StepProps = React.HTMLAttributes<HTMLDivElement> & {
  active?: boolean;
  completed?: boolean;
  disabled?: boolean;
  index?: number; // Injetado pelo Stepper
  isLast?: boolean; // Injetado pelo Stepper
  children?: React.ReactNode;
}

export function Step({
  active = false,
  completed = false,
  disabled = false,
  index = 0,
  isLast = false,
  children,
  className = "",
  ...props
}: StepProps): JSX.Element {
  const { orientation } = useStepperContext();

  return (
    <StepContext.Provider value={{ index, active, completed, isLast }}>
      <div
        className={`${
          orientation === "vertical" ? "flex flex-col" : "flex-1 relative"
        } ${disabled ? "opacity-50 pointer-events-none" : ""} ${className}`}
        {...props}
      >
        {children}
      </div>
    </StepContext.Provider>
  );
}

// --- STEP LABEL ---
export type StepLabelProps = React.HTMLAttributes<HTMLDivElement> & {
  children?: React.ReactNode;
  StepIconComponent?: React.ElementType;
}

export function StepLabel({
  children,
  StepIconComponent,
  className = "",
  ...props
}: StepLabelProps): JSX.Element {
  const { orientation, alternativeLabel } = useStepperContext();
  const { index, active, completed } = useStepContext();

  // Ícone padrão caso não seja fornecido um customizado
  const DefaultIcon = () => (
    <div
      className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium transition-colors ${
        active
          ? "bg-blue-600 text-white"
          : completed
            ? "bg-blue-600 text-white"
            : "bg-gray-300 text-gray-600"
      }`}
    >
      {completed ? (
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      ) : (
        index + 1
      )}
    </div>
  );

  const IconToRender = StepIconComponent ? (
    <StepIconComponent active={active} completed={completed} icon={index + 1} />
  ) : (
    <DefaultIcon />
  );

  return (
    <div
      className={`flex py-1 ${
        orientation === "vertical"
          ? "flex-row items-center text-left"
          : alternativeLabel
            ? "flex-col items-center text-center"
            : "flex-row items-center text-left"
      } ${className}`}
      {...props}
    >
      <span className="flex items-center justify-center pr-2">{IconToRender}</span>
      <span
        className={`text-sm font-medium transition-colors ${
          active ? "text-gray-900" : "text-gray-500"
        }`}
      >
        {children}
      </span>
    </div>
  );
}

// --- STEP CONTENT ---
export interface StepContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

export function StepContent({ children, className = "", ...props }: StepContentProps): JSX.Element {
  const { orientation } = useStepperContext();
  const { active, isLast } = useStepContext();

  if (orientation !== "vertical") {
    console.warn("<StepContent> deve ser usado apenas em <Stepper orientation=\"vertical\">.");

    return <></>;
  }

  return (
    // A margem esquerda (ml-3) alinha a borda exatamente no meio do ícone de 24px (12px = 3 unidades no tailwind)
    <div
      className={`ml-3 pl-5 ${
        !isLast ? "border-l-2 border-gray-200" : "border-l-2 border-transparent"
      } ${className}`}
      {...props}
    >
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          active ? "grid-rows-[1fr] opacity-100 py-3" : "grid-rows-[0fr] opacity-0 py-0"
        }`}
      >
        <div className="overflow-hidden">{children}</div>
      </div>
    </div>
  );
}